import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, ArrowRight, Loader2, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
// Placeholder key - user must replace this
const WEB3FORMS_ACCESS_KEY = 'YOUR_NEW_ACCESS_KEY_HERE';
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  inquiryType: string;
  message: string;
}
interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
}
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
const INQUIRY_TYPES = ['Market Entry Strategy', 'Supply Chain Optimization', 'Trade Compliance', 'Risk Management', 'Partnership Inquiry', 'General Inquiry'];
export function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: INQUIRY_TYPES[0],
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          ...formData,
          subject: `New Inquiry: ${formData.inquiryType} from ${formData.firstName} ${formData.lastName}`
        })
      });
      const result = await response.json();
      if (result.success) {
        setStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          inquiryType: INQUIRY_TYPES[0],
          message: ''
        });
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to send message. Please check your connection and try again.');
    }
  };
  const resetForm = () => {
    setStatus('idle');
    setErrors({});
  };
  return <section className="relative py-24 bg-[#0a1628] overflow-hidden" data-id="element-162">
      {/* Diagonal Split Background */}
      <div className="absolute inset-0 bg-[#d4a853] w-full lg:w-[40%] h-full lg:right-0 lg:left-auto left-0 top-0 hidden lg:block" style={{
      clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0% 100%)'
    }} data-id="element-163" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-164">
        <div className="flex flex-col lg:flex-row gap-16" data-id="element-165">
          {/* Left Content */}
          <div className="w-full lg:w-1/2 pt-12" data-id="element-166">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-167">
              <div className="flex items-center gap-3 mb-6" data-id="element-168">
                <div className="h-px w-12 bg-[#d4a853]" data-id="element-169" />
                <span className="text-[#d4a853] font-medium tracking-[0.2em] uppercase text-sm" data-id="element-170">
                  Get In Touch
                </span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-171">
                Ready to Source <br data-id="element-172" /> or Expand?
              </h2>

              <p className="text-gray-400 text-lg mb-12 max-w-md" data-id="element-173">
                Whether you are a retailer seeking reliable sourcing or a
                manufacturer looking to enter new markets, we are ready to
                partner with you.
              </p>

              <div className="space-y-8" data-id="element-174">
                <div className="flex items-start gap-4" data-id="element-175">
                  <div className="w-12 h-12 bg-[#1a2d4a] flex items-center justify-center flex-shrink-0" data-id="element-176">
                    <Mail className="text-[#d4a853] w-5 h-5" data-id="element-177" />
                  </div>
                  <div data-id="element-178">
                    <h3 className="text-white font-bold mb-1" data-id="element-179">Email Us</h3>
                    <p className="text-gray-400" data-id="element-180">
                      consult@888sourcingltd.co.uk
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-181">
                  <div className="w-12 h-12 bg-[#1a2d4a] flex items-center justify-center flex-shrink-0" data-id="element-182">
                    <Phone className="text-[#d4a853] w-5 h-5" data-id="element-183" />
                  </div>
                  <div data-id="element-184">
                    <h3 className="text-white font-bold mb-1" data-id="element-185">Call Us</h3>
                    <p className="text-gray-400" data-id="element-186">+44 (0) 1784 693046</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-187">
                  <div className="w-12 h-12 bg-[#1a2d4a] flex items-center justify-center flex-shrink-0" data-id="element-188">
                    <MapPin className="text-[#d4a853] w-5 h-5" data-id="element-189" />
                  </div>
                  <div data-id="element-190">
                    <h3 className="text-white font-bold mb-1" data-id="element-191">Visit Us</h3>
                    <p className="text-gray-400" data-id="element-192">
                      Vermeulens Garden Centre
                      <br data-id="element-193" />
                      Horton Road, Staines-Upon-Thames
                      <br data-id="element-194" />
                      Middlesex, TW19 6AE
                      <br data-id="element-195" />
                      United Kingdom
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Form */}
          <div className="w-full lg:w-1/2 lg:pl-12" data-id="element-196">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-[#1a2d4a] p-8 lg:p-12 border border-white/10 shadow-2xl relative min-h-[600px] flex flex-col justify-center" data-id="element-197">
              <AnimatePresence mode="wait" data-id="element-198">
                {status === 'success' ? <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="text-center py-12" data-id="element-199">
                    <div className="w-20 h-20 bg-[#d4a853]/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-200">
                      <CheckCircle className="w-10 h-10 text-[#d4a853]" data-id="element-201" />
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4" data-id="element-202">
                      Message Sent!
                    </h3>
                    <p className="text-gray-400 mb-8 max-w-sm mx-auto" data-id="element-203">
                      Thank you for contacting us. We've received your inquiry
                      and will get back to you within 24 hours.
                    </p>
                    <button onClick={resetForm} className="inline-flex items-center justify-center px-8 py-3 bg-[#0a1628] border border-[#d4a853] text-[#d4a853] font-bold uppercase tracking-wider hover:bg-[#d4a853] hover:text-[#0a1628] transition-colors" data-id="element-204">
                      Send Another Message
                    </button>
                  </motion.div> : <motion.div key="form" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-205">
                    <h3 className="text-2xl font-bold text-white mb-6" data-id="element-206">
                      Send us a message
                    </h3>

                    {status === 'error' && <div className="mb-6 bg-red-500/10 border border-red-500/20 p-4 flex items-start gap-3 rounded" data-id="element-207">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-208" />
                        <div data-id="element-209">
                          <h4 className="text-red-500 font-bold text-sm" data-id="element-210">
                            Error Sending Message
                          </h4>
                          <p className="text-red-400 text-sm mt-1" data-id="element-211">
                            {errorMessage}
                          </p>
                        </div>
                      </div>}

                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-212">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-213">
                        <div className="space-y-2" data-id="element-214">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-215">
                            First Name <span className="text-[#d4a853]" data-id="element-216">*</span>
                          </label>
                          <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} disabled={status === 'submitting'} className={`w-full bg-[#0a1628] border p-3 text-white focus:outline-none transition-colors ${errors.firstName ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#d4a853]'}`} placeholder="John" data-id="element-217" />
                          {errors.firstName && <p className="text-red-500 text-xs mt-1" data-id="element-218">
                              {errors.firstName}
                            </p>}
                        </div>
                        <div className="space-y-2" data-id="element-219">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-220">
                            Last Name <span className="text-[#d4a853]" data-id="element-221">*</span>
                          </label>
                          <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} disabled={status === 'submitting'} className={`w-full bg-[#0a1628] border p-3 text-white focus:outline-none transition-colors ${errors.lastName ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#d4a853]'}`} placeholder="Doe" data-id="element-222" />
                          {errors.lastName && <p className="text-red-500 text-xs mt-1" data-id="element-223">
                              {errors.lastName}
                            </p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-224">
                        <div className="space-y-2" data-id="element-225">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-226">
                            Email Address{' '}
                            <span className="text-[#d4a853]" data-id="element-227">*</span>
                          </label>
                          <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={status === 'submitting'} className={`w-full bg-[#0a1628] border p-3 text-white focus:outline-none transition-colors ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#d4a853]'}`} placeholder="john@company.com" data-id="element-228" />
                          {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-229">
                              {errors.email}
                            </p>}
                        </div>
                        <div className="space-y-2" data-id="element-230">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-231">
                            Phone Number
                          </label>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={status === 'submitting'} className="w-full bg-[#0a1628] border border-white/10 p-3 text-white focus:border-[#d4a853] focus:outline-none transition-colors" placeholder="+1 (555) 000-0000" data-id="element-232" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-233">
                        <div className="space-y-2" data-id="element-234">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-235">
                            Company
                          </label>
                          <input type="text" name="company" value={formData.company} onChange={handleChange} disabled={status === 'submitting'} className="w-full bg-[#0a1628] border border-white/10 p-3 text-white focus:border-[#d4a853] focus:outline-none transition-colors" placeholder="Company Ltd." data-id="element-236" />
                        </div>
                        <div className="space-y-2" data-id="element-237">
                          <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-238">
                            Inquiry Type
                          </label>
                          <div className="relative" data-id="element-239">
                            <select name="inquiryType" value={formData.inquiryType} onChange={handleChange} disabled={status === 'submitting'} className="w-full bg-[#0a1628] border border-white/10 p-3 text-white focus:border-[#d4a853] focus:outline-none transition-colors appearance-none cursor-pointer" data-id="element-240">
                              {INQUIRY_TYPES.map(type => <option key={type} value={type} data-id="element-241">
                                  {type}
                                </option>)}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-242" />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2" data-id="element-243">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-244">
                          Message <span className="text-[#d4a853]" data-id="element-245">*</span>
                        </label>
                        <textarea name="message" rows={4} value={formData.message} onChange={handleChange} disabled={status === 'submitting'} className={`w-full bg-[#0a1628] border p-3 text-white focus:outline-none transition-colors resize-none ${errors.message ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-[#d4a853]'}`} placeholder="Tell us about your project..." data-id="element-246" />
                        {errors.message && <p className="text-red-500 text-xs mt-1" data-id="element-247">
                            {errors.message}
                          </p>}
                      </div>

                      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#d4a853] text-[#0a1628] font-bold uppercase tracking-wider py-4 hover:bg-white transition-colors flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-248">
                        {status === 'submitting' ? <>
                            <Loader2 className="w-4 h-4 animate-spin" data-id="element-249" />
                            Sending...
                          </> : <>
                            Send Message
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-250" />
                          </>}
                      </button>
                    </form>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}